// ==========================================================================
// Onboarding Page - Modal-based benefits showcase with detail views
// Main modal shows benefit options, clicking opens detail views with
// slide transitions. Accept TOS from any screen via "Accept & continue".
// Welcome screen background visible behind the modal overlay.
// ==========================================================================

import { backgroundImage, weatherIcon, poweredByAtram, welcomeCarouselImage, carouselBackground } from '@/assets';
import { useAcceptTos, useUser, userKeys } from '@/hooks/useUser';
import { track } from '@/lib/mixpanel';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { filterBenefitsForFsp, type BenefitOption } from './benefitVisibility';
import {
  X,
  ArrowRight,
  ArrowUp,
  ThermometerSun,
  Megaphone,
  Lightbulb,
  Coins,
  Banknote,
} from 'lucide-react';

type ModalView = 'benefits' | 'forecast' | 'alerts' | 'protection' | 'loans';

const DETAIL_ICONS: Record<string, React.ReactNode> = {
  forecast: <ThermometerSun className="w-12 h-12 text-white" strokeWidth={1.5} />,
  alerts: <Megaphone className="w-12 h-12 text-white" strokeWidth={1.5} />,
  protection: (
    <div className="flex items-center gap-2">
      <Lightbulb className="w-10 h-10 text-white" strokeWidth={1.5} />
      <span className="text-white/60 text-lg">+</span>
      <Coins className="w-10 h-10 text-white" strokeWidth={1.5} />
    </div>
  ),
  loans: (
    <div className="relative">
      <Banknote className="w-12 h-12 text-white" strokeWidth={1.5} />
      <ArrowUp className="w-5 h-5 text-white absolute -top-2 -right-2" strokeWidth={2} />
    </div>
  ),
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: user } = useUser();
  const acceptTosMutation = useAcceptTos();
  const queryClient = useQueryClient();

  // FSP branding
  const fsp = user?.fsp;
  const primaryColor = fsp?.primary_color || '#ED974F';
  const secondaryColor = fsp?.secondary_color || '#c47535';
  const fspName = fsp?.name || 'Atram';
  const isAtram = fspName === 'Atram';

  // Navigation state - simple view switching
  const [currentView, setCurrentView] = useState<ModalView>('benefits');
  const [direction, setDirection] = useState<'right' | 'left'>('right');
  const [animKey, setAnimKey] = useState(0);

  const isDetailView = currentView !== 'benefits';

  // Terms URL
  const termsUrl =
    fsp?.tos_link ||
    (i18n.language === 'es'
      ? 'https://atram.ai/terminos-y-condiciones.pdf'
      : 'https://atram.ai/terms-and-conditions.pdf');

  // Navigate to a detail view (slides right)
  const navigateTo = useCallback((view: ModalView) => {
    track('FE Onboarding Option Tapped', { option: view });
    setDirection('right');
    setAnimKey((k) => k + 1);
    setCurrentView(view);
  }, []);

  // Go back to benefits screen (slides left)
  const goToMain = useCallback(() => {
    setDirection('left');
    setAnimKey((k) => k + 1);
    setCurrentView('benefits');
  }, []);

  // Close modal -> back to welcome screen
  const closeModal = useCallback(() => {
    navigate('/welcome');
  }, [navigate]);

  // Accept TOS and proceed to location setup
  const handleAccept = useCallback(async () => {
    try {
      await acceptTosMutation.mutateAsync();
      track('FE Onboarding Completed', { accepted_from: currentView });
      // Refetch user to guarantee onboarded=true is in cache before navigating
      await queryClient.refetchQueries({ queryKey: userKeys.detail() });
      navigate('/location-setup');
    } catch {
      // Toast shown by axios interceptor — stay on onboarding if TOS accept fails
    }
  }, [acceptTosMutation, navigate, queryClient]);

  // Benefits visibility is per-FSP — see ./benefitVisibility.ts.
  // Opt-out semantics: missing/null flags keep the benefit visible; only an
  // explicit `false` on `fsp.benefits.<flag>` hides it.
  const benefitOptions: BenefitOption[] = filterBenefitsForFsp(
    [
      { key: 'forecast', label: t('onboarding.options.forecast') },
      { key: 'alerts', label: t('onboarding.options.alerts') },
      { key: 'protection', label: t('onboarding.options.protection') },
      { key: 'loans', label: t('onboarding.options.loans') },
    ],
    fsp?.benefits,
  );

  return (
    <div className="fixed inset-0 z-0">
      {/* Welcome screen in background */}
      <div
        className="absolute inset-0 flex flex-col bg-cover bg-center bg-no-repeat"
        style={
          isAtram
            ? { backgroundImage: `url(${backgroundImage})` }
            : {
                background: `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`,
              }
        }
      >
        {/* Top: Logo */}
        <div className="pt-12 px-6 flex items-center justify-center pb-16 min-h-[86px]">
          {fsp?.appIcon && (
            <img
              src={fsp.appIcon}
              alt={fspName}
              className="w-[54px] h-[54px] rounded-full object-cover border-0 shadow-none mx-auto"
            />
          )}
        </div>

        {/* Center Weather Icon */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <img
            src={weatherIcon}
            alt="Weather"
            className="w-[140px] h-[140px] object-contain"
          />
        </div>

        {/* Bottom Content */}
        <div className="px-6 pb-6 text-center">
          <h2
            className="text-[28px] font-bold leading-tight text-black"
          >
            {t('welcome.protectWeather')}
          </h2>
          <h2
            className="text-[28px] font-bold leading-tight mb-4 text-black"
          >
            {t('welcome.protectBusiness')}
          </h2>

          <p
            className="text-[16px] leading-relaxed mb-8 text-black/60"
          >
            {t('welcome.description')}
          </p>

          {/* Powered by Atram */}
          <div className="flex items-center justify-center">
            <img
              src={poweredByAtram}
              alt="Powered by Atram"
              className="h-[30px] object-contain"
            />
          </div>
        </div>
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/30 z-40" />

      {/* Modal container - aligned to bottom */}
      <div className="absolute inset-0 z-50 flex items-end justify-center p-4 pb-5">
        {/* Modal card */}
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-xl overflow-hidden max-h-[92vh]">
        <div
          key={animKey}
          className={
            animKey > 0
              ? direction === 'right'
                ? 'onboarding-slide-right'
                : 'onboarding-slide-left'
              : 'fade-in'
          }
        >
          {!isDetailView ? (
            /* ===== BENEFITS VIEW ===== */
            <div>
              {/* Hero background with image */}
              <div className="relative h-[130px] overflow-hidden bg-gray-100 rounded-t-3xl flex items-end justify-center">
                <img
                  src={welcomeCarouselImage}
                  alt=""
                  className="h-[120px] object-contain"
                />
                {/* Close button */}
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center z-10"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

            <div className="px-6 pt-5 pb-6">

              {/* Title */}
              <h1 className="text-[22px] font-bold text-gray-900 leading-tight mb-5">
                {t('onboarding.benefitsTitle', { bankName: fspName })}
              </h1>

              {/* Options list */}
              <div className="flex flex-col gap-2.5 mb-5">
                {benefitOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => navigateTo(option.key)}
                    className="w-full flex items-center justify-between p-4 text-left rounded-xl bg-gray-100"
                  >
                    <span className="text-[15px] text-gray-800 leading-snug pr-4">
                      {option.label}
                    </span>
                    <ArrowRight className="w-5 h-5 text-gray-900 flex-shrink-0" strokeWidth={2.5} />
                  </button>
                ))}
              </div>

              {/* Terms text */}
              <p className="text-[11px] text-gray-500 leading-relaxed mb-4">
                {t('onboarding.terms.text')}{' '}
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-gray-700"
                >
                  {t('onboarding.terms.termsLink')}
                </a>{' '}
                {t('onboarding.terms.and')}{' '}
                <span className="text-gray-400">{t('onboarding.terms.privacyLink')}</span>.
              </p>


              {/* Accept button */}
              <button
                type="button"
                onClick={handleAccept}
                disabled={acceptTosMutation.isPending}
                className="w-full h-[48px] rounded-xl text-white text-[15px] font-semibold disabled:opacity-60 flex items-center justify-center gap-1.5"
                style={{ backgroundColor: primaryColor }}
              >
                {acceptTosMutation.isPending ? t('common.loading') : t('onboarding.acceptAndContinue')}
              </button>
            </div>
            </div>
          ) : (
            /* ===== DETAIL VIEW ===== */
            <div>
              {/* Hero background with icon + nav buttons */}
              <div className="relative h-[200px] overflow-hidden">
                <img
                  src={carouselBackground}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {/* Icon centered on background */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {DETAIL_ICONS[currentView]}
                </div>
                {/* Close button */}
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pt-6 pb-6">

                {/* Title with bold keyword */}
                <h2 className="text-[22px] font-normal text-gray-900 leading-tight mb-3">
                  <Trans
                    i18nKey={`onboarding.slides.${currentView}.title`}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </h2>

                {/* Description */}
                <p className="text-[14px] text-gray-500 leading-relaxed mb-8">
                  {t(`onboarding.slides.${currentView}.description`, { bankName: fspName })}
                </p>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goToMain}
                    className="flex-1 h-[46px] rounded-xl border border-gray-200 bg-white text-gray-900 text-[13px] font-semibold"
                  >
                    {t('onboarding.backToTerms')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    disabled={acceptTosMutation.isPending}
                    className="flex-1 h-[46px] rounded-xl text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {acceptTosMutation.isPending
                      ? t('common.loading')
                      : t('onboarding.acceptAndContinue')}
                      </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
